import { z } from 'zod';
import {
  BooleanQuery,
  Id,
  IsoDate,
  PaginationQuery,
  paginated,
  queryArray,
  sortQuery,
} from './common';
import { ProductType } from './enums';

// Seeds (`api.md` § Référentiel, T09). Les messages sont des clés i18n rendues par `t()` côté web.

export const SeedType = z.enum(['keywords', 'url', 'site', 'keywords_url']);
export type SeedType = z.infer<typeof SeedType>;

export const SEED_KEYWORDS_MIN = 1;
export const SEED_KEYWORDS_MAX = 20;
export const SEED_KEYWORD_MAX_LENGTH = 80;
export const SEED_LABEL_MIN_LENGTH = 2;
export const SEED_LABEL_MAX_LENGTH = 120;
export const SEED_CATEGORY_MAX_LENGTH = 40;
export const SEED_TAG_MAX_LENGTH = 30;
export const SEED_TAGS_MAX = 10;
export const SEED_NOTES_MAX_LENGTH = 2000;
export const SEED_IMPORT_MAX_ROWS = 500;
export const SEED_SORT_FIELDS = ['label', 'lastUsedAt', 'createdAt'] as const;
export type SeedSortField = (typeof SEED_SORT_FIELDS)[number];

export function seedTypeNeedsKeywords(type: SeedType): boolean {
  return type === 'keywords' || type === 'keywords_url';
}

export function seedTypeNeedsUrl(type: SeedType): boolean {
  return type === 'url' || type === 'site' || type === 'keywords_url';
}

/** `trim().toLowerCase()`, vides retirés, doublons fusionnés (ordre d'apparition conservé). */
export function normalizeKeywords(keywords: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of keywords) {
    const keyword = raw.trim().toLowerCase();
    if (keyword.length === 0 || seen.has(keyword)) continue;
    seen.add(keyword);
    out.push(keyword);
  }
  return out;
}

/** Tags en minuscules, vides retirés, dédoublonnés. */
export function normalizeTags(tags: readonly string[]): string[] {
  return normalizeKeywords(tags);
}

// Longueur des requêtes (T37). Keyword Planner ne dérive des idées qu'autour d'expressions
// réellement tapées : une seed doit contenir des requêtes de 1 à 4 mots (cible 2–3), pas des
// phrases-tâches. Constat du 2026-09-08 : 12 phrases de 5 à 8 mots → 12 volumes nuls, 0 idée.

/** Au-delà, un mot-clé est une phrase : rejeté par les aides, signalé dans le formulaire. */
export const SEED_KEYWORD_MAX_WORDS = 5;
/** Longueur recommandée d'une requête (mots), rappelée dans l'avertissement. */
export const SEED_KEYWORD_TARGET_MIN_WORDS = 2;
export const SEED_KEYWORD_TARGET_MAX_WORDS = 4;

/** Nombre de mots d'une requête (séparés par des espaces, après `trim`). */
export function keywordWordCount(keyword: string): number {
  const trimmed = keyword.trim();
  return trimmed.length === 0 ? 0 : trimmed.split(/\s+/).length;
}

/** Moyenne de mots par mot-clé, arrondie au dixième ; `null` sans mot-clé. */
export function averageKeywordWords(keywords: readonly string[]): number | null {
  const counted = keywords.map(keywordWordCount).filter((n) => n > 0);
  if (counted.length === 0) return null;
  return Math.round((counted.reduce((sum, n) => sum + n, 0) / counted.length) * 10) / 10;
}

/** Mots-clés de plus de `SEED_KEYWORD_MAX_WORDS` mots (des phrases, pas des requêtes). */
export function longKeywords(keywords: readonly string[]): string[] {
  return keywords.filter((keyword) => keywordWordCount(keyword) > SEED_KEYWORD_MAX_WORDS);
}

/**
 * Vrai quand la liste ressemble à des phrases plutôt qu'à des requêtes : moyenne au-dessus de
 * `SEED_KEYWORD_TARGET_MAX_WORDS` mots, ou au moins un terme au-delà de `SEED_KEYWORD_MAX_WORDS`.
 */
export function hasLongKeywords(keywords: readonly string[]): boolean {
  const average = averageKeywordWords(keywords);
  return (
    (average !== null && average > SEED_KEYWORD_TARGET_MAX_WORDS) ||
    longKeywords(keywords).length > 0
  );
}

/** Vérifie la règle « 1 à 20 mots-clés » sur la liste nettoyée. */
export function keywordsIssue(keywords: readonly string[] | undefined): string | null {
  const cleaned = normalizeKeywords(keywords ?? []);
  if (cleaned.length < SEED_KEYWORDS_MIN) return 'validation.keywords1to20';
  if (cleaned.length > SEED_KEYWORDS_MAX) return 'validation.max20Keywords';
  return null;
}

const Keyword = z.string().trim().max(SEED_KEYWORD_MAX_LENGTH, 'validation.maxLength');
const Label = z
  .string()
  .trim()
  .min(SEED_LABEL_MIN_LENGTH, 'validation.minLength')
  .max(SEED_LABEL_MAX_LENGTH, 'validation.maxLength');
const Category = z.string().trim().max(SEED_CATEGORY_MAX_LENGTH, 'validation.maxLength');
const Tag = z
  .string()
  .trim()
  .min(1, 'validation.required')
  .max(SEED_TAG_MAX_LENGTH, 'validation.maxLength');
const Tags = z.array(Tag).max(SEED_TAGS_MAX, 'validation.max');

/** Ligne renvoyée par l'API (jointure `user` pour `createdBy`). */
export const SeedRow = z.object({
  id: Id,
  marketId: z.string(),
  type: SeedType,
  label: z.string(),
  keywords: z.array(z.string()),
  url: z.string().nullable(),
  category: z.string().nullable(),
  tags: z.array(z.string()),
  notes: z.string().nullable(),
  isActive: z.boolean(),
  createdBy: z.object({ id: z.string(), name: z.string() }),
  lastUsedAt: IsoDate.nullable(),
  createdAt: IsoDate,
  updatedAt: IsoDate.nullable(),
});
export type SeedRow = z.infer<typeof SeedRow>;

export const SeedsResponse = paginated(SeedRow);
export type SeedsResponse = z.infer<typeof SeedsResponse>;

/** `GET /seeds` : filtres et tri (`type` répété ou unique, `isActive=true|false`). */
export const SeedsQuery = PaginationQuery.extend(sortQuery(SEED_SORT_FIELDS).shape).extend({
  marketId: z.string().optional(),
  type: queryArray(SeedType).optional(),
  category: z.string().max(SEED_CATEGORY_MAX_LENGTH).optional(),
  isActive: BooleanQuery.optional(),
  q: z.string().trim().max(80).optional(),
});
export type SeedsQuery = z.infer<typeof SeedsQuery>;
export type SeedsQueryInput = z.input<typeof SeedsQuery>;

/**
 * Champs d'un seed sans la règle croisée type ↔ contenu (`superRefine` ci-dessous). zod 4 refuse
 * `.partial()` sur un objet raffiné : `UpdateSeedBody` dérive de cette base et le service revalide
 * la fusion existant + patch avec `CreateSeedBody`.
 */
export const SeedFields = z.object({
  marketId: z.string().min(1, 'validation.required'),
  type: SeedType,
  label: Label,
  keywords: z.array(Keyword).optional(),
  url: z.url('validation.url').optional(),
  category: Category.optional(),
  tags: Tags.default([]),
  notes: z.string().max(SEED_NOTES_MAX_LENGTH, 'validation.maxLength').optional(),
});
export type SeedFields = z.infer<typeof SeedFields>;

function refineSeed(
  value: { type: SeedType; keywords?: readonly string[] | undefined; url?: string | undefined },
  ctx: z.RefinementCtx,
): void {
  if (seedTypeNeedsKeywords(value.type)) {
    const issue = keywordsIssue(value.keywords);
    if (issue) ctx.addIssue({ path: ['keywords'], code: 'custom', message: issue });
  }
  if (seedTypeNeedsUrl(value.type) && !value.url) {
    ctx.addIssue({ path: ['url'], code: 'custom', message: 'validation.required' });
  }
}

/** `POST /seeds`. */
export const CreateSeedBody = SeedFields.superRefine(refineSeed);
export type CreateSeedBody = z.infer<typeof CreateSeedBody>;

/** `PATCH /seeds/:id` : champs éditables, `marketId` exclu. */
export const UpdateSeedBody = SeedFields.partial()
  .omit({ marketId: true })
  // `.partial()` conserve le `default([])` de `tags` : un PATCH sans `tags` ne doit pas les vider.
  .extend({ tags: Tags.optional(), isActive: z.boolean().optional() });
export type UpdateSeedBody = z.infer<typeof UpdateSeedBody>;

/**
 * `POST /seeds/import`. Les lignes sont volontairement peu contraintes : le service valide chaque
 * ligne (libellé, 1 à 20 mots-clés nettoyés) et remplit `errors[]` sans interrompre l'import.
 */
export const ImportSeedRow = z.object({
  label: z.string().trim().max(SEED_LABEL_MAX_LENGTH),
  keywords: z.array(z.string().trim().max(SEED_KEYWORD_MAX_LENGTH)).max(100),
  category: Category.optional(),
});
export type ImportSeedRow = z.infer<typeof ImportSeedRow>;

export const ImportSeedsBody = z.object({
  marketId: z.string().min(1, 'validation.required'),
  rows: z.array(ImportSeedRow).min(1).max(SEED_IMPORT_MAX_ROWS),
});
export type ImportSeedsBody = z.infer<typeof ImportSeedsBody>;

export const ImportSeedsResponse = z.object({
  created: z.number().int(),
  updated: z.number().int(),
  errors: z.array(z.object({ row: z.number().int(), message: z.string() })),
});
export type ImportSeedsResponse = z.infer<typeof ImportSeedsResponse>;

// ---------------------------------------------------------------------------
// Aides à la création de seed (T36, `api.md` § Référentiel) : analyse de site et assistant
// ---------------------------------------------------------------------------

/** Coût indicatif d'un tour d'assistant (borne haute d'un appel LLM, `llm.md` § Budget). */
export const SEED_ASSIST_COST_HINT_USD = 0.01;
/** Coût indicatif d'une requête DataForSEO `search_volume` (T37, 0,09 $ la requête). */
export const KEYWORD_VOLUMES_COST_HINT_USD = 0.09;
/** Coût indicatif d'une analyse de site : appel LLM + volumes réels (T37). */
export const SITE_ANALYSIS_COST_HINT_USD = 0.1;
/** Mots-clés par requête de volumes (`POST /seeds/keyword-volumes`). */
export const KEYWORD_VOLUMES_MAX = 100;
export const SEED_ASSISTANT_MAX_MESSAGES = 12;
export const SEED_ASSISTANT_MESSAGE_MAX_LENGTH = 2000;

/** Concurrence Google Ads d'un mot-clé (`KeywordIdea.competition`). */
export const KeywordCompetition = z.enum(['LOW', 'MEDIUM', 'HIGH']);
export type KeywordCompetition = z.infer<typeof KeywordCompetition>;

/** `POST /seeds/analyze-site`. */
export const AnalyzeSiteBody = z.object({
  marketId: z.string().min(1, 'validation.required'),
  url: z.url('validation.url'),
});
export type AnalyzeSiteBody = z.infer<typeof AnalyzeSiteBody>;

/** `site` : tâche explicitement servie par le site ; `adjacent` : besoin voisin plausible. */
export const SiteKeywordOrigin = z.enum(['site', 'adjacent']);
export type SiteKeywordOrigin = z.infer<typeof SiteKeywordOrigin>;

export const AnalyzeSiteKeyword = z.object({
  text: z.string(),
  origin: SiteKeywordOrigin,
  /** Volume mensuel moyen (`search_volume`, T37) ; `null` si le fournisseur n'a rien renvoyé. */
  avgMonthlySearches: z.number().int().nullable(),
  competition: KeywordCompetition.nullable(),
});
export type AnalyzeSiteKeyword = z.infer<typeof AnalyzeSiteKeyword>;

export const AnalyzeSiteResponse = z.object({
  summary: z.string(),
  productType: ProductType,
  label: z.string(),
  /** Dans l'ordre du modèle (du plus générique au plus précis) ; le client trie par volume. */
  keywords: z.array(AnalyzeSiteKeyword),
  /** Catégorie existante de la bibliothèque, sinon `null`. */
  category: z.string().nullable(),
  /** Tags existants de la bibliothèque. */
  tags: z.array(z.string()),
  /** Seuil de demande du marché (`markets.min_volume`, porte G1 : 50 fr-FR, 200 en-US). */
  minVolume: z.number().int(),
  /** Coût réel total : appel LLM + requête de volumes (la lecture du site est gratuite). */
  costUsd: z.number(),
  costs: z.object({ llmUsd: z.number(), volumesUsd: z.number() }),
  source: z.object({
    fetchedUrl: z.string(),
    /** Caractères de texte visible transmis au modèle, toutes pages confondues. */
    chars: z.number().int(),
    truncated: z.boolean(),
    /** Pages lues (accueil + tarifs / fonctionnalités, au plus 3). */
    pages: z.number().int(),
  }),
});
export type AnalyzeSiteResponse = z.infer<typeof AnalyzeSiteResponse>;

export const SeedAssistantRole = z.enum(['user', 'assistant']);
export type SeedAssistantRole = z.infer<typeof SeedAssistantRole>;

export const SeedAssistantMessage = z.object({
  role: SeedAssistantRole,
  content: z
    .string()
    .trim()
    .min(1, 'validation.required')
    .max(SEED_ASSISTANT_MESSAGE_MAX_LENGTH, 'validation.maxLength'),
});
export type SeedAssistantMessage = z.infer<typeof SeedAssistantMessage>;

/** Brouillon de seed échangé avec l'assistant (valeurs du formulaire, jamais enregistrées ici). */
export const SeedAssistantDraft = z.object({
  marketId: z.string(),
  type: SeedType,
  label: z.string(),
  keywords: z.array(z.string()),
  url: z.string().nullable(),
  category: z.string().nullable(),
  tags: z.array(z.string()),
  notes: z.string(),
});
export type SeedAssistantDraft = z.infer<typeof SeedAssistantDraft>;

/** Volume connu d'un terme du brouillon, transmis à l'assistant pour qu'il corrige (T37). */
export const SeedKeywordVolume = z.object({
  text: z.string().trim().min(1).max(SEED_KEYWORD_MAX_LENGTH),
  avgMonthlySearches: z.number().int().nullable(),
});
export type SeedKeywordVolume = z.infer<typeof SeedKeywordVolume>;

/**
 * `POST /seeds/assistant` : ≤ 12 messages de ≤ 2 000 caractères, brouillon courant ou `null`,
 * volumes vérifiés des termes du brouillon (facultatif).
 */
export const SeedAssistantBody = z.object({
  marketId: z.string().min(1, 'validation.required'),
  messages: z
    .array(SeedAssistantMessage)
    .min(1, 'validation.required')
    .max(SEED_ASSISTANT_MAX_MESSAGES, 'validation.max'),
  draft: SeedAssistantDraft.nullable(),
  volumes: z.array(SeedKeywordVolume).max(KEYWORD_VOLUMES_MAX, 'validation.max').optional(),
});
export type SeedAssistantBody = z.infer<typeof SeedAssistantBody>;

export const SeedAssistantResponse = z.object({
  reply: z.string(),
  draft: SeedAssistantDraft,
  ready: z.boolean(),
  costUsd: z.number(),
});
export type SeedAssistantResponse = z.infer<typeof SeedAssistantResponse>;

/** `POST /seeds/keyword-volumes` (T37, payant 0,09 $) : volumes réels de 1 à 100 mots-clés. */
export const KeywordVolumesBody = z.object({
  marketId: z.string().min(1, 'validation.required'),
  keywords: z
    .array(z.string().trim().min(1, 'validation.required').max(SEED_KEYWORD_MAX_LENGTH))
    .min(1, 'validation.required')
    .max(KEYWORD_VOLUMES_MAX, 'validation.max'),
});
export type KeywordVolumesBody = z.infer<typeof KeywordVolumesBody>;

export const KeywordVolumeItem = z.object({
  text: z.string(),
  avgMonthlySearches: z.number().int().nullable(),
  competition: KeywordCompetition.nullable(),
});
export type KeywordVolumeItem = z.infer<typeof KeywordVolumeItem>;

export const KeywordVolumesResponse = z.object({
  /** Un item par mot-clé demandé (forme normalisée), dans l'ordre de la demande. */
  items: z.array(KeywordVolumeItem),
  minVolume: z.number().int(),
  costUsd: z.number(),
});
export type KeywordVolumesResponse = z.infer<typeof KeywordVolumesResponse>;
